import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, Button, Modal, LoadingSkeleton } from '../ui';
import { ArrowLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../../utils/apiError';
import useApi from '../portal/useApi';
import { formatDate } from '../portal/format';
import { downloadFile } from '../portal/download';
import ReportView, { examTypeLabel, EXAM_TYPE_LABELS } from './ReportView';
import CommentTemplatePicker from './CommentTemplatePicker';
import portal from '../portal/Portal.module.css';
import styles from './Reports.module.css';

const NETWORK_ERROR = 'Network error. Please check your connection and try again.';
const MAX_COMMENT = 1000;
const TRANSITIONS = {
  submit: { label: 'Submit for finalisation', done: 'Report submitted for finalisation.', confirm: 'Submit this report for finalisation? You will no longer be able to change its marks or comment unless the Head of Academics returns it.' },
  return: { label: 'Return to teacher', done: 'Report returned to the class teacher.', confirm: 'Return this report to the class teacher as a draft so they can change it?' },
  publish: { label: 'Publish', done: 'Report published. The parent can now see it.', confirm: 'Publish this report? It is frozen as it stands and becomes visible to the student\'s parents.' },
  unpublish: { label: 'Unpublish', done: 'Report unpublished.', confirm: 'Unpublish this report? Parents lose sight of it and of this term\'s marks until it is published again.' },
};

async function send(token, method, url, body) {
  return fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

// One subject's marks and remark, editable. Saves each changed assessment;
// the remark is kept on the end-of-term mark (or the last one recorded),
// which is the one the report prints.
function SubjectEditor({ token, subject, examTypes, templates, onSaved, onError }) {
  const remarkHolder = subject.assessments.find((item) => item.exam_type === 'end_term') || subject.assessments[subject.assessments.length - 1];
  const initialScores = () => Object.fromEntries(subject.assessments.map((item) => [item.id, item.score ?? '']));
  const [scores, setScores] = useState(initialScores);
  const [remark, setRemark] = useState(subject.remark || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setScores(initialScores());
    setRemark(subject.remark || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);

  const byType = Object.fromEntries(subject.assessments.map((item) => [item.exam_type, item]));
  const dirty = subject.assessments.some((item) => String(scores[item.id]) !== String(item.score ?? '')) || remark !== (subject.remark || '');

  const handleSave = async () => {
    setSaving(true);
    onError('');
    try {
      for (const item of subject.assessments) {
        const body = {};
        if (String(scores[item.id]) !== String(item.score ?? '')) body.score = scores[item.id];
        if (item.id === remarkHolder.id && remark !== (subject.remark || '')) body.remarks = remark;
        if (Object.keys(body).length === 0) continue;
        const response = await send(token, 'PUT', `/api/v1/exam-results/${item.id}`, body);
        if (!response.ok) {
          onError(`${subject.subject}: ${await safeErrorMessage(response, 'Failed to save')}`);
          return;
        }
      }
      onSaved();
    } catch {
      onError(NETWORK_ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td><strong>{subject.subject}</strong></td>
      {examTypes.map((type) => (
        <td key={type} className={styles.num}>
          {byType[type] ? (
            <input
              type="number" min="0" max="100" step="0.5" className={styles.scoreInput}
              value={scores[byType[type].id] ?? ''}
              onChange={(e) => setScores((prev) => ({ ...prev, [byType[type].id]: e.target.value }))}
              aria-label={`${subject.subject} ${examTypeLabel(type)} mark`}
            />
          ) : '—'}
        </td>
      ))}
      <td>
        <div className={styles.remarkCell}>
          <input type="text" className={styles.remarkInput} maxLength={200} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Teacher's remark" aria-label={`${subject.subject} remark`} />
          <CommentTemplatePicker groups={templates?.subject} bands={templates?.bands} mark={subject.final_mark} onPick={setRemark} label="Pick a ready-made remark…" />
        </div>
      </td>
      <td>
        <Button variant={dirty ? 'primary' : 'secondary'} size="sm" onClick={handleSave} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </td>
    </tr>
  );
}

function AddMark({ token, report, onSaved, onError }) {
  const [form, setForm] = useState({ subject_id: '', exam_type: 'end_term', score: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    onError('');
    try {
      const response = await send(token, 'POST', '/api/v1/exam-results', {
        student_id: report.student.id, term_id: report.term.id, subject_id: Number(form.subject_id), exam_type: form.exam_type, score: form.score,
      });
      if (response.ok) {
        setForm({ subject_id: '', exam_type: 'end_term', score: '' });
        onSaved();
      } else {
        onError(await safeErrorMessage(response, 'Failed to add mark'));
      }
    } catch {
      onError(NETWORK_ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.addMark} onSubmit={handleAdd}>
      <label>
        Add a missing mark
        <select value={form.subject_id} onChange={(e) => setForm((prev) => ({ ...prev, subject_id: e.target.value }))} required>
          <option value="">Subject</option>
          {(report.all_subjects || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
      <label>
        Assessment
        <select value={form.exam_type} onChange={(e) => setForm((prev) => ({ ...prev, exam_type: e.target.value }))}>
          {Object.entries(EXAM_TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label>
        Mark (%)
        <input type="number" min="0" max="100" step="0.5" className={styles.scoreInput} value={form.score} onChange={(e) => setForm((prev) => ({ ...prev, score: e.target.value }))} required />
      </label>
      <Button variant="secondary" size="sm" type="submit" disabled={saving}>{saving ? 'Adding…' : 'Add mark'}</Button>
    </form>
  );
}

function CommentEditor({ id, label, value, groups, bands, average, saving, onSave }) {
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);
  const dirty = text !== (value || '');

  return (
    <div className={styles.commentEditor}>
      <label htmlFor={id}>{label}</label>
      <textarea id={id} className={styles.textarea} rows={3} maxLength={MAX_COMMENT} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment, or pick a ready-made one below and adjust it." />
      <div className={styles.editorFoot}>
        <CommentTemplatePicker groups={groups} bands={bands} mark={average} onPick={setText} />
        <Button variant={dirty ? 'primary' : 'secondary'} size="sm" onClick={() => onSave(text)} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save comment'}</Button>
        <span className={styles.counter}>{text.length}/{MAX_COMMENT}</span>
      </div>
    </div>
  );
}

// Review one student's term report: see it as it will print, correct marks
// and remarks, write comments, and move it along (submit / return /
// publish / unpublish). What is editable comes from `report.can`, which
// the backend works out from the user's role and the report's status.
function ReportCardReview({ token, user }) {
  const { id } = useParams();
  const card = useApi(token, `/api/v1/reports/cards/${id}`);
  const templates = useApi(token, '/api/v1/reports/cards/comment-templates');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [savingComment, setSavingComment] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [working, setWorking] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const report = card.data;
  const can = report?.can || {};

  const saveComment = async (field, text) => {
    setSavingComment(field);
    setError('');
    try {
      const response = await send(token, 'PUT', `/api/v1/reports/cards/${id}`, { [field]: text });
      if (response.ok) card.reload();
      else setError(await safeErrorMessage(response, 'Failed to save comment'));
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setSavingComment('');
    }
  };

  const runTransition = async () => {
    const action = confirming;
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const response = await send(token, 'POST', `/api/v1/reports/cards/${action}`, { ids: [Number(id)] });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.changed > 0) {
        setNotice(TRANSITIONS[action].done);
        card.reload();
      } else {
        setError(data.skipped?.[0]?.reason || data.error || 'That could not be done.');
      }
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setWorking(false);
      setConfirming(null);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError(await downloadFile(token, `/api/v1/reports/cards/${id}/pdf`, 'report.pdf'));
    setDownloading(false);
  };

  if (card.loading && !report) return <LoadingSkeleton type="text" rows={10} />;
  if (!report) return <div className="alert alert-error">{card.error || 'Report not found'}</div>;

  const stage = report.status === 'published'
    ? `Published ${formatDate(report.published_at)}${report.published_by_name ? ` by ${report.published_by_name}` : ''}. Visible to parents and frozen; unpublish it to make changes.`
    : report.status === 'submitted'
      ? `Submitted ${formatDate(report.submitted_at)}${report.submitted_by_name ? ` by ${report.submitted_by_name}` : ''}. Waiting for the Head of Academics to finalise and publish. Parents cannot see it yet.`
      : 'Draft. Teachers can correct marks and remarks. Parents cannot see it yet.';

  return (
    <div>
      <Link to="/report-cards" className={portal.backLink}>
        <ArrowLeftIcon style={{ width: 14, height: 14 }} />
        Report Cards
      </Link>
      <div className={portal.header}>
        <div>
          <h2 className={portal.title}>{report.student.full_name}</h2>
          <p className={portal.subtitle}>{report.student.class_name} · {report.term.label}</p>
        </div>
        <div className={styles.actions}>
          {['submit', 'return', 'publish', 'unpublish'].filter((action) => can[action]).map((action) => (
            <Button key={action} variant={action === 'publish' || action === 'submit' ? 'primary' : 'secondary'} size="sm" onClick={() => setConfirming(action)}>
              {TRANSITIONS[action].label}
            </Button>
          ))}
          <Button variant="secondary" size="sm" onClick={handleDownload} disabled={downloading}>
            <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
            {downloading ? 'Preparing…' : 'Download PDF'}
          </Button>
        </div>
      </div>

      <p className={styles.statusLine}>{stage}</p>
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <ReportView report={report} schoolName={user?.school_name} />

      {can.edit_marks && (
        <Card title="Correct marks and remarks" className={styles.step}>
          <p className={styles.hint}>Change a mark or remark and press Save on that row. You can only save subjects you teach in this class, unless you are the class teacher or Head of Academics.</p>
          {report.subjects.length === 0 ? (
            <p className={styles.empty}>No marks have been recorded for this term yet.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.marks}>
                <thead>
                  <tr>
                    <th>Subject</th>
                    {report.exam_types.map((type) => <th key={type} className={styles.num}>{examTypeLabel(type)}</th>)}
                    <th>Teacher's remark</th>
                    <th aria-label="Save" />
                  </tr>
                </thead>
                <tbody>
                  {report.subjects.map((subject) => (
                    <SubjectEditor key={subject.subject_id} token={token} subject={subject} examTypes={report.exam_types} templates={templates.data} onSaved={card.reload} onError={setError} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <AddMark token={token} report={report} onSaved={card.reload} onError={setError} />
        </Card>
      )}

      {(can.edit_class_teacher_comment || can.edit_head_comment) && (
        <Card title="Comments" className={styles.step}>
          {can.edit_class_teacher_comment && (
            <CommentEditor
              id="class-teacher-comment" label="Class teacher's comment" value={report.class_teacher_comment}
              groups={templates.data?.class_teacher} bands={templates.data?.bands} average={report.average}
              saving={savingComment === 'class_teacher_comment'} onSave={(text) => saveComment('class_teacher_comment', text)}
            />
          )}
          {can.edit_head_comment && (
            <CommentEditor
              id="head-comment" label="Head's comment" value={report.head_comment}
              groups={templates.data?.head} bands={templates.data?.bands} average={report.average}
              saving={savingComment === 'head_comment'} onSave={(text) => saveComment('head_comment', text)}
            />
          )}
        </Card>
      )}

      <Modal isOpen={Boolean(confirming)} onClose={() => setConfirming(null)} title={confirming ? TRANSITIONS[confirming].label : ''}>
        {confirming && (
          <div>
            <p className={styles.hint}>{TRANSITIONS[confirming].confirm}</p>
            <div className={styles.actions}>
              <Button variant="primary" onClick={runTransition} disabled={working}>{working ? 'Working…' : TRANSITIONS[confirming].label}</Button>
              <Button variant="secondary" onClick={() => setConfirming(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ReportCardReview;
